import React, { useEffect } from 'react'
import { HashRouter as Router, Route, Routes } from 'react-router-dom'
import NotFoundPage from './pages/NotFoundPage'
import { AUTHENTICATED_ROUTES, UNAUTHENTICATED_ROUTES } from './resources/routes-constants'
import './styles/main.sass'
import { useAppDispatch, useAppSelector } from './store/reducers/store'
import HomeUnauthenticated from './pages/unauthenticated/HomeUnauthenticated'
import AnonymousLayout from './layouts/AnonymousLayout'
import MainLayout from './layouts/MainLayout'
import HomePage from './pages/authenticated/HomePage'
import { refreshToken } from './store/actions/authentication'
import { Box, CircularProgress, Typography } from '@mui/material'
import PortfoliosPage from './pages/authenticated/PortfoliosPage'
import ListOfFundsPage from './pages/authenticated/ListOfFundsPage'
import CreatePortfolioPage from './pages/authenticated/CreatePortfolioPage'
import PortfolioDetails from './pages/authenticated/PortfolioDetails'
import PortfolioTrackingPage from './pages/authenticated/PortfolioTrackingPage'
import SubmitPortfolioPage from './pages/authenticated/SubmitPortfolioPage'
import PortfolioDetailsInvestors from './pages/authenticated/PortfolioDetailsInvestors'
import PortfolioDetailsReports from './pages/authenticated/PortfolioDetailsReports'
import ListOfFundsReportsPage from './pages/authenticated/ListOfFundsReportsPage'
import SubmittedPortfolioPage from './pages/authenticated/SubmittedPortfolioPage'
import PortfolioSummaryPage from './pages/authenticated/PortfolioSummaryPage'
import { useTranslation } from 'react-i18next'
import SettingsPage from './pages/authenticated/SettingsPage'

const RootComponent: React.FC = () => {
    const account = useAppSelector(state => state.authentication.token);
    const expiresOn = useAppSelector(state => state.authentication.expiresOn);
    const authenticating = useAppSelector(state => state.authentication.authenticating);
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const isTokenExpired = !authenticating && account && expiresOn && expiresOn < Date.now();

    useEffect(() => {
        if (isTokenExpired) {
            dispatch(refreshToken());
        }
    }, [isTokenExpired, dispatch])

    return (
        account ?
            <Router>
                <MainLayout>
                    {isTokenExpired ?
                        <Box display={"flex"} flexDirection={"column"} width={"100%"} height={"100%"} justifyContent={"center"} alignItems={"center"} gap={"20px"}>
                            <CircularProgress />
                            <Typography>{t('GeneralGettingThingsUp')}</Typography>
                        </Box>
                        :
                        <Routes>
                            <Route path={AUTHENTICATED_ROUTES.HOMEPAGE_ROUTE} element={<HomePage />} />
                            <Route path={AUTHENTICATED_ROUTES.PORTFOLIOS_ROUTE}>
                                <Route path={AUTHENTICATED_ROUTES.PORTFOLIO_CREATE_ROUTE}>
                                    <Route path={AUTHENTICATED_ROUTES.PORTFOLIO_CREATE_WITH_FUND_ROUTE} element={<CreatePortfolioPage />} />
                                    <Route path={AUTHENTICATED_ROUTES.PORTFOLIO_CREATE_STEP_ROUTE} element={<CreatePortfolioPage />} />
                                    <Route path={AUTHENTICATED_ROUTES.PORTFOLIO_CREATE_ROUTE} element={<CreatePortfolioPage />} />
                                </Route>
                                <Route path={AUTHENTICATED_ROUTES.PORTFOLIO_EDIT_ROUTE}>
                                    <Route path={AUTHENTICATED_ROUTES.PORTFOLIO_EDIT_STEP_ROUTE} element={<CreatePortfolioPage />} />
                                    <Route path={AUTHENTICATED_ROUTES.PORTFOLIO_EDIT_ROUTE} element={<CreatePortfolioPage />} />
                                </Route>
                                <Route path={AUTHENTICATED_ROUTES.PORTFOLIO_DETAILS_ROUTE}>
                                    <Route path={AUTHENTICATED_ROUTES.PORTFOLIO_DETAILS_INVESTORS_ROUTE} element={<PortfolioDetailsInvestors />} />
                                    <Route path={AUTHENTICATED_ROUTES.PORTFOLIO_DETAILS_REPORTS_ROUTE} element={<PortfolioDetailsReports />} />
                                    <Route path={AUTHENTICATED_ROUTES.PORTFOLIO_DETAILS_ROUTE} element={<PortfolioDetails />} />
                                </Route>
                                <Route path={AUTHENTICATED_ROUTES.PORTFOLIO_SUBMIT_ROUTE} element={<SubmitPortfolioPage />} />
                                <Route path={AUTHENTICATED_ROUTES.PORTFOLIO_SUBMITTED_ROUTE} element={<SubmittedPortfolioPage />} />
                                <Route path={AUTHENTICATED_ROUTES.PORTFOLIO_TRACKING_ROUTE} element={<PortfolioTrackingPage />} />
                                <Route path={AUTHENTICATED_ROUTES.PORTFOLIO_SUMMARY_ROUTE} element={<PortfolioSummaryPage />} />
                                <Route path={AUTHENTICATED_ROUTES.PORTFOLIO_ADD_TO_ROUTE} element={<PortfoliosPage isAddToExisting />} />
                                <Route path={AUTHENTICATED_ROUTES.PORTFOLIOS_CONFIRM_SAVE_ROUTE} element={<PortfoliosPage />} />
                                <Route path={AUTHENTICATED_ROUTES.PORTFOLIOS_ROUTE} element={<PortfoliosPage />} />
                            </Route>
                            <Route path={AUTHENTICATED_ROUTES.LIBRARYOFFUNDS_ROUTE}>
                                <Route path={AUTHENTICATED_ROUTES.LIBRARYOFFUNDS_ITEM_ROUTE} element={<ListOfFundsPage />} />
                                <Route path={AUTHENTICATED_ROUTES.LIBRARYOFFUNDS_ITEM_DATAROOM_ROUTE} element={<ListOfFundsReportsPage />} />
                                <Route path={AUTHENTICATED_ROUTES.LIBRARYOFFUNDS_ROUTE} element={<ListOfFundsPage />} />
                            </Route>
                            <Route path={AUTHENTICATED_ROUTES.SETTING_ROUTE} element={<SettingsPage />} />
                            <Route path="*" element={<NotFoundPage />} />
                        </Routes>
                    }
                </MainLayout>
            </Router>
            :
            <AnonymousLayout>
                {isTokenExpired ?
                    <div>
                        <CircularProgress />
                        <Typography>Getting everything up and running...</Typography>
                    </div>
                    :
                    <Router>
                        <Routes>
                            <Route path={UNAUTHENTICATED_ROUTES.HOMEPAGE_ROUTE} element={<HomeUnauthenticated />} />
                            <Route path="*" element={<NotFoundPage />} />
                        </Routes>
                    </Router>}
            </AnonymousLayout>
    );
}

export default RootComponent;
